/**
 * Time Formatting Utilities
 * Relative time display with absolute tooltip support
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = Date.now();
  const diff = now - date.getTime();

  if (diff < 0) {
    return 'w przyszłości';
  }

  if (diff < MINUTE) {
    return 'przed chwilą';
  }

  if (diff < HOUR) {
    const minutes = Math.floor(diff / MINUTE);
    return `${minutes} min temu`;
  }

  if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return `${hours} godz. temu`;
  }

  if (diff < WEEK) {
    const days = Math.floor(diff / DAY);
    return `${days} ${days === 1 ? 'dzień' : 'dni'} temu`;
  }

  if (diff < MONTH) {
    const weeks = Math.floor(diff / WEEK);
    return `${weeks} ${weeks === 1 ? 'tydzień' : 'tygodni'} temu`;
  }

  const months = Math.floor(diff / MONTH);
  return `${months} ${months === 1 ? 'miesiąc' : 'miesięcy'} temu`;
}

/**
 * Format a date as absolute time for tooltips
 */
export function formatAbsoluteTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Timestamp component props
 */
export type TimestampProps = {
  value: string | null;
  fallback?: string;
};

/**
 * Create timestamp display with relative time and absolute tooltip
 */
export function formatTimestamp(value: string | null, fallback = '-'): {
  relative: string;
  absolute: string;
} {
  if (!value) {
    return { relative: fallback, absolute: fallback };
  }

  return {
    relative: formatRelativeTime(value),
    absolute: formatAbsoluteTime(value),
  };
}
